import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/button";
import { Loader } from "@/components/Loader";
import { usePageTitle } from "@/hooks/usePageTitle";

import {
  isAlphaRunActive,
  useAlphaRunsQuery,
} from "../hooks/useAlphaRunsQuery";
import { AlphaStepLayout } from "../layout/AlphaStepLayout";
import { startAlphaStepTransition } from "../layout/alphaStepTransition";
import { AlphaReviewScreen } from "../review/AlphaReviewScreen";
import { AlphaGenerationScreen } from "./AlphaGenerationScreen";

type Screen = "error" | "generating" | "loading" | "review";

export function AlphaRunPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as {
    requestId?: number | string;
  };
  const requestId = Number(params.requestId);

  usePageTitle(`Design drafts - #${requestId}`);

  const { data, error, isLoading } = useAlphaRunsQuery(requestId);
  const latestRun = data?.runs[0];

  const screen = getScreen({
    hasError: Boolean(error),
    isGenerating: Boolean(latestRun && isAlphaRunActive(latestRun)),
    isLoading,
  });
  const [shownScreen, setShownScreen] = useState(screen);

  useEffect(() => {
    if (shownScreen !== screen) {
      startAlphaStepTransition(() => setShownScreen(screen));
    }
  }, [screen, shownScreen]);

  return (
    <div className="p-xl">
      <AlphaStepLayout
        actions={
          <Button
            color="gray"
            onPress={() =>
              void navigate({ to: "/ai-ad-studio", viewTransition: true })
            }
            variant="tertiary"
          >
            Save and exit
          </Button>
        }
        activeStep="Review"
      >
        {shownScreen === "loading" && (
          <div className="p-3xl">
            <Loader />
          </div>
        )}
        {shownScreen === "error" && (
          <p className="p-3xl text-sm text-text-error-primary">
            Couldn&apos;t load your designs. Please refresh the page.
          </p>
        )}
        {shownScreen === "generating" && latestRun && (
          <AlphaGenerationScreen run={latestRun} />
        )}
        {shownScreen === "review" && data && (
          <AlphaReviewScreen requestId={requestId} runs={data.runs} />
        )}
      </AlphaStepLayout>
    </div>
  );
}

function getScreen({
  hasError,
  isGenerating,
  isLoading,
}: {
  hasError: boolean;
  isGenerating: boolean;
  isLoading: boolean;
}): Screen {
  if (hasError) return "error";

  if (isLoading) return "loading";

  return isGenerating ? "generating" : "review";
}
